// src/components/QuestionTypes/TestQuestionInterface.js
// 概要：仕訳入力型の試験用コンポーネント（テーブル形式）
// 仕様：
//  - 設問本文（ルビ対応）の表示
//  - 借方/貸方の勘定科目と金額をテーブルで入力
//  - 最終設問でのボタン文言は「提出して見直す」、途中は「確認して次へ」
//  - フォームの初期行数は「模範解答の行数」または table.rows（なければ1）
//  - disabled プロップで入力と送信ボタンを無効化できる（時間切れ等）
//
// 依存：Ant Design（Form, Table, Select, InputNumber など）

import React, { useEffect, useMemo } from 'react';
import {
  Typography,
  Select,
  InputNumber,
  Table,
  Button,
  Form,
} from 'antd';
import { CheckCircleOutlined, RightOutlined } from '@ant-design/icons';
import './JournalEntryQuestion.css';

const { Paragraph } = Typography;
const { Option } = Select;

/* ================= ルビ付きテキスト表示 ================= */
const RubyText = ({ parts }) => (
  <Paragraph className="question-content">
    {Array.isArray(parts) &&
      parts.map((part, index) =>
        part.type === 'ruby' ? (
          <ruby key={index}>
            {part.text}
            <rt>{part.ruby}</rt>
          </ruby>
        ) : (
          <span key={index}>{part.value}</span>
        )
      )}
  </Paragraph>
);

/* ================= メインコンポーネント ================= */
const TestQuestionInterface = ({
  question,
  exerciseId,
  onAnswerSubmit,
  isLastQuestion,
  form,
  // 時間切れ等で外部から無効化したい場合に使用
  disabled = false,
}) => {
  // 初期行数：模範解答の行数 > table.rows > 1
  const initialRowCount = useMemo(() => {
    if (Array.isArray(question?.answer)) return Math.max(1, question.answer.length);
    if (typeof question?.table?.rows === 'number' && question.table.rows > 0) {
      return question.table.rows;
    }
    return 1;
  }, [question]);

  // フォーム初期値（各行は空オブジェクトで作成）
  const initialRows = useMemo(
    () => Array.from({ length: initialRowCount }, () => ({})),
    [initialRowCount]
  );

  useEffect(() => {
    // ここでは reset しない：見直しで戻った際の入力保持のため
  }, [exerciseId, form]);

  // セレクト用オプション（安全化）
  const options = Array.isArray(question?.table?.options) ? question.table.options : [];

  // 金額のフォーマッタ/パーサ（3桁区切り・数字のみ）
  const formatNumber = (value) => `${value ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const parseNumber = (value) =>
    value ? Number(value.replace(/\$\s?|(,*)/g, '').replace(/[^0-9]/g, '')) : 0;

  // テーブル列定義
  const columns = [
    {
      title: '借方科目',
      width: '25%',
      render: (field) => (
        <Form.Item name={[field.name, 'debitAccount']} noStyle>
          <Select
            placeholder="勘定科目を選択"
            style={{ width: '100%' }}
            disabled={disabled}
          >
            {options.map((opt) => (
              <Option key={opt} value={opt}>
                {opt}
              </Option>
            ))}
          </Select>
        </Form.Item>
      ),
    },
    {
      title: '金額',
      width: '25%',
      render: (field) => (
        <Form.Item name={[field.name, 'debitAmount']} noStyle>
          <InputNumber
            placeholder="金額を入力"
            style={{ width: '100%' }}
            formatter={formatNumber}
            parser={(v) => parseNumber(v)}
            disabled={disabled}
          />
        </Form.Item>
      ),
    },
    {
      title: '貸方科目',
      width: '25%',
      render: (field) => (
        <Form.Item name={[field.name, 'creditAccount']} noStyle>
          <Select
            placeholder="勘定科目を選択"
            style={{ width: '100%' }}
            disabled={disabled}
          >
            {options.map((opt) => (
              <Option key={opt} value={opt}>
                {opt}
              </Option>
            ))}
          </Select>
        </Form.Item>
      ),
    },
    {
      title: '金額',
      width: '25%',
      render: (field) => (
        <Form.Item name={[field.name, 'creditAmount']} noStyle>
          <InputNumber
            placeholder="金額を入力"
            style={{ width: '100%' }}
            formatter={formatNumber}
            parser={(v) => parseNumber(v)}
            disabled={disabled}
          />
        </Form.Item>
      ),
    },
  ];

  return (
    <div>
      {/* 設問本文（ルビ対応） */}
      <RubyText parts={question?.content?.parts} />

      {/* 仕訳入力フォーム */}
      <Form
        form={form}
        name={`journal_entry_${exerciseId}`}
        onFinish={onAnswerSubmit}
        autoComplete="off"
      >
        <Form.List name="entries" initialValue={initialRows}>
          {(fields) => (
            <Table
              rowKey={(field) => field.key}
              columns={columns}
              dataSource={fields}
              pagination={false}
              bordered
            />
          )}
        </Form.List>

        {/* 送信ボタン（時間切れ等で無効化可能） */}
        <Form.Item style={{ marginTop: 24, textAlign: 'right' }}>
          <Button
            type="primary"
            htmlType="submit"
            icon={isLastQuestion ? <CheckCircleOutlined /> : <RightOutlined />}
            disabled={disabled}
          >
            {isLastQuestion ? '提出して見直す' : '確認して次へ'}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default TestQuestionInterface;
